import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Divider,
  Drawer,
  Link,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import { FC, useCallback, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import { Maybe, ThreadBoxFragment, User } from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { usePrivateAction } from '@/lib/privateAction'
import { LinkifyBox } from '@/lib/reactLinkify'

import { ThreadCategory } from '../ThreadCategory'

export type ThreadDetailBoxProps = {
  thread: ThreadBoxFragment
  currentUser: Maybe<Pick<User, 'formattedAccountId'>>
  paddingImage?: number
  onLikeOrUnlike: (isLike: boolean, likeCount: number) => void
  onReportUser: (
    threadId: string,
    userId: string,
    userAccountId: string
  ) => Promise<void>
  onBlockUser: (userId: string) => Promise<void | boolean>
  onDeleteThread: (id: string) => Promise<void>
  onReply?: () => void
}

export const ThreadDetailBox: FC<ThreadDetailBoxProps> = ({
  thread,
  currentUser,
  paddingImage = 0,
  onLikeOrUnlike,
  onReportUser,
  onBlockUser,
  onDeleteThread,
  onReply,
}) => {
  const { t } = useTranslation('threadDetail')
  const { handleLoginModal } = usePrivateAction()
  const { showMessage, showError } = useMessage()
  const { generatePath, routerPages } = useRouter()
  const [open, setOpen] = useState<boolean>(false)

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  const handleReportUser = useCallback(async () => {
    await onReportUser(
      thread.id,
      thread.createdByUser?.id ?? '',
      thread.createdByUser?.formattedAccountId ?? ''
    )
    setOpen(false)
  }, [onReportUser, thread])

  const handleBlockUser = useCallback(async () => {
    await onBlockUser(thread.createdByUser?.id ?? '').then(
      hiddenMessage =>
        !hiddenMessage && showMessage(t('blockUserSuccess'), 'success')
    )
    setOpen(false)
  }, [onBlockUser, showMessage, t, thread.createdByUser])

  const handleDeleteThread = useCallback(async () => {
    try {
      await onDeleteThread(thread.id)
      showMessage(t('deleteComplete'), 'success')
    } catch (error) {
      if (error instanceof Error) showError(error)
    }
  }, [onDeleteThread, showError, showMessage, t, thread.id])

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        borderRadius: 0,
        zIndex: 1,
        position: 'relative',
      }}
    >
      {thread.thumbnail?.mediumUri && (
        <Box sx={{ px: paddingImage }}>
          <CardMedia
            component="img"
            height="280"
            image={thread.thumbnail.mediumUri}
            alt={thread.name}
          />
        </Box>
      )}
      <CardContent>
        <Typography variant="h4">{thread.name}</Typography>
        {!(!thread.isOfficial && !thread.category) && (
          <ThreadCategory
            isOfficial={thread.isOfficial}
            category={thread.category ?? ''}
          />
        )}
        {thread.createdByUser && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pt: 5 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack
                component={Link}
                onClick={handleLoginModal()}
                href={generatePath(routerPages.UserProfile, {
                  userId: thread.createdByUser?.id,
                })}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <Avatar
                  src={thread.createdByUser?.avatar?.smallUri ?? ''}
                  sx={{ width: 28, height: 28 }}
                />
                <Typography variant="subtitle2" color="text.secondary">
                  {thread.createdByUser?.displayName}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.hint">
                {thread.formattedCreatedAt}
              </Typography>
            </Stack>
            {/**
             * @description
             * Open when function is implemented in the future
             * https://app.clickup.com/t/908127/UT-5129
             */}

            <IconButton.More size="small" onClick={toggleDrawer(true)} />
          </Stack>
        )}
        <LinkifyBox>
          <Typography variant="body1" sx={{ pt: 3, wordBreak: 'break-all' }}>
            {thread.description}
          </Typography>
        </LinkifyBox>
      </CardContent>
      <CardActions
        sx={{
          pt: 0,
          pb: 3,
          px: 5,
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={5}
        >
          <Stack direction="row" alignItems="center">
            <IconButton.Like
              onClick={handleLoginModal(() =>
                onLikeOrUnlike(!!thread.isLike, thread.likeCount)
              )}
              liked={!!thread.isLike}
            />
            <Typography variant="caption">{thread.likeCount}</Typography>
          </Stack>
          {onReply && (
            <Stack direction="row" alignItems="center">
              <IconButton.Reply onClick={handleLoginModal(onReply)} />
              <Typography variant="caption">{thread.commentCount}</Typography>
            </Stack>
          )}
        </Stack>
      </CardActions>
      <Drawer disableEnforceFocus open={open} onClose={toggleDrawer(false)}>
        <Box sx={{ p: 5 }}>
          <Typography variant="h6" color="grey.600" align="center">
            {thread.createdByUser?.displayName}
          </Typography>
        </Box>
        <Divider />
        <List>
          {currentUser?.formattedAccountId !==
          thread.createdByUser?.formattedAccountId ? (
            <>
              <ListItemButton
                component={Box}
                sx={{ height: 52 }}
                onClick={handleLoginModal(handleReportUser)}
              >
                <Typography variant="h6">{t('report')}</Typography>
              </ListItemButton>
              <ListItemButton
                component={Box}
                sx={{ height: 52 }}
                divider
                onClick={handleLoginModal(handleBlockUser)}
              >
                <Typography variant="h6">{t('block')}</Typography>
              </ListItemButton>
            </>
          ) : (
            <>
              <ListItemButton
                component={Link}
                onClick={handleLoginModal()}
                sx={{ height: 52 }}
                href={generatePath(routerPages.ArtistThreadEdit, {
                  threadId: thread.id,
                  artistId: thread.createdByUser?.id,
                })}
              >
                <Typography variant="h6">{t('edit')}</Typography>
              </ListItemButton>
              <ListItemButton
                component={Box}
                sx={{ height: 52 }}
                divider
                onClick={handleLoginModal(handleDeleteThread)}
              >
                <Typography variant="h6">{t('delete')}</Typography>
              </ListItemButton>
            </>
          )}

          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            alignItems="center"
            onClick={toggleDrawer(false)}
          >
            <Typography variant="h6" align="center" sx={{ width: '100%' }}>
              {t('cancel')}
            </Typography>
          </ListItemButton>
        </List>
      </Drawer>
    </Card>
  )
}
